<template>
  <header
    class="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-5 shrink-0">
    <div class="flex items-center gap-3">
      <button @click="$emit('toggle-sidebar')" class="text-gray-500 hover:text-gray-800 transition lg:hidden">
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <nav class="hidden sm:flex items-center gap-1 text-sm text-gray-500">
        <router-link :to="homeRoute" class="hover:text-blue-600 transition"
          >Asosiy</router-link
        >
        <template v-if="breadcrumb">
          <span class="mx-1">/</span>
          <span class="text-gray-800 font-medium">{{ breadcrumb }}</span>
        </template>
      </nav>
    </div>

    <!-- Right: user -->
    <div class="relative">
      <div class="flex items-center gap-3 cursor-pointer select-none group" @click="showDropdown = !showDropdown">
        <div class="text-right">
          <div class="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition">
            {{ user.fullName }}
          </div>
          <div class="text-xs text-gray-400 capitalize">{{ user.role }}</div>
        </div>
        <div
          v-if="user.avatar"
          class="w-9 h-9 rounded-full shadow-sm overflow-hidden border border-gray-200">
          <img :src="user.avatar" alt="Avatar" class="w-full h-full object-cover" />
        </div>
        <div
          v-else
          class="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
          {{ userInitials }}
        </div>
      </div>

      <!-- Dropdown -->
      <div v-if="showDropdown" class="absolute right-0 mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-xl py-1 z-50" style="animation: fadeIn 0.15s ease">
        
        <!-- Rollar ro'yxati -->
        <template v-if="user.roles && user.roles.length > 0">
          <div class="px-4 py-2 text-xs text-gray-400 font-medium uppercase tracking-wider border-b border-gray-100">Rollar</div>
          <button 
            v-for="r in user.roles" 
            :key="r.code"
            @click="switchRole(r)"
            class="w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition"
            :class="user.role === r.name ? 'text-blue-600 bg-blue-50 font-medium' : 'text-gray-700 hover:bg-gray-50'"
          >
            <svg v-if="user.role === r.name" class="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
            <span v-else class="w-4 h-4"></span>
            {{ r.name }}
          </button>
          <div class="border-t border-gray-100"></div>
        </template>

        <!-- Login va parolni o'zgartirish tugmasi: FAQAT oquvsuperadmin uchun -->
        <template v-if="isOquvSuperAdmin">
          <button 
            @click="openPasswordModal" 
            class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2 transition"
          >
            <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Login / Parol o'zgartirish
          </button>
          <div class="border-t border-gray-100"></div>
        </template>

        <button @click="logout" class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Chiqish
        </button>
      </div>
    </div>

    <!-- Login va parolni o'zgartirish modali -->
    <div v-if="showPasswordModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div class="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-gray-800" style="animation: scaleUp 0.18s ease">
        <div class="flex items-center justify-between border-b pb-3 mb-4">
          <div class="flex items-center gap-2">
            <div class="p-2 bg-blue-100 text-blue-600 rounded-lg">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-bold text-gray-800">Login va parolni o'zgartirish</h3>
              <p class="text-xs text-gray-500">Xavfsizlik parametrlarini yangilash</p>
            </div>
          </div>
          <button @click="closePasswordModal" class="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <form @submit.prevent="handlePasswordChange" class="space-y-4">
          <!-- Super Admin boshqa adminni tanlash imkoniyati -->
          <div v-if="isSuperAdmin && allAdmins.length > 1" class="mb-3">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Qaysi hisobni o'zgartirmoqchisiz?</label>
            <select v-model="changeTarget" @change="onTargetChange" class="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="self">O'zimning hisobim ({{ user.phone || user.fullName }})</option>
              <option v-for="adm in otherAdmins" :key="adm._id" :value="adm._id">
                {{ adm.name }} ({{ adm.phone }} - {{ adm.role }})
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Foydalanuvchi nomi (Login)</label>
            <input 
              v-model="pwdForm.newPhone" 
              type="text" 
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
              placeholder="Login kiriting (masalan: oquvsuperadmin)"
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Ism / Tavsif</label>
            <input 
              v-model="pwdForm.newName" 
              type="text" 
              class="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
              placeholder="Masalan: oquvsuper admin"
            />
          </div>

          <!-- Joriy parol (o'zining hisobini o'zgartirganda kerak) -->
          <div v-if="changeTarget === 'self'">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Joriy (eski) parol</label>
            <div class="relative">
              <input 
                v-model="pwdForm.currentPassword" 
                :type="showCurrPass ? 'text' : 'password'" 
                required
                class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Eski parolingizni kiriting"
              />
              <button type="button" @click="showCurrPass = !showCurrPass" class="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                <svg v-if="showCurrPass" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"/></svg>
              </button>
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Yangi parol</label>
            <div class="relative">
              <input 
                v-model="pwdForm.newPassword" 
                :type="showNewPass ? 'text' : 'password'" 
                required
                minlength="4"
                class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Kamida 4 ta belgi"
              />
              <button type="button" @click="showNewPass = !showNewPass" class="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                <svg v-if="showNewPass" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"/></svg>
              </button>
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Yangi parolni tasdiqlang</label>
            <input 
              v-model="pwdForm.confirmPassword" 
              type="password" 
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
              placeholder="Yangi parolni qayta tering"
            />
          </div>

          <div v-if="pwdError" class="p-3 bg-red-50 text-red-600 text-xs rounded-xl border border-red-200">
            {{ pwdError }}
          </div>
          <div v-if="pwdSuccess" class="p-3 bg-green-50 text-green-700 text-xs rounded-xl border border-green-200">
            {{ pwdSuccess }}
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t">
            <button 
              type="button" 
              @click="closePasswordModal" 
              class="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition"
            >
              Bekor qilish
            </button>
            <button 
              type="submit" 
              :disabled="pwdLoading"
              class="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl shadow transition flex items-center gap-2"
            >
              <span v-if="pwdLoading" class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              Saqlash
            </button>
          </div>
        </form>
      </div>
    </div>
  </header>
</template>

<script>
import axios from "axios";

export default {
  name: "AppHeader",
  data() {
    return {
      user: {
        id: null,
        phone: "",
        fullName: "Super Admin",
        role: "admin",
        avatar: null,
        roles: [],
      },
      showDropdown: false,
      showPasswordModal: false,
      showCurrPass: false,
      showNewPass: false,
      pwdLoading: false,
      pwdError: "",
      pwdSuccess: "",
      changeTarget: "self",
      allAdmins: [],
      pwdForm: {
        newPhone: "",
        newName: "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
    };
  },
  mounted() {
    const data = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (data && data !== "undefined") {
      try {
        const parsed = JSON.parse(data);
        if (parsed) {
          if (
            (parsed.phone === 'j_raxmankulov' || (parsed.name && parsed.name.toLowerCase().includes('raxmankulov'))) &&
            Number(parsed.departmentId) !== 12
          ) {
            parsed.departmentId = 12;
            sessionStorage.setItem("user", JSON.stringify(parsed));
          }
          this.user.id = parsed.id || parsed._id || null;
          this.user.phone = parsed.phone || "";
          this.user.fullName = parsed.name || parsed.fullName || "Foydalanuvchi";
          this.user.role = parsed.role || "admin";
          this.user.avatar = parsed.avatar || null;
          this.user.roles = parsed.roles || [];
        }
      } catch (e) {
        console.error("User data parse error", e);
      }
    }
  },
  methods: {
    switchRole(r) {
      this.user.role = r.name;
      const data = sessionStorage.getItem("user") || localStorage.getItem("user");
      if (data) {
        try {
          const parsed = JSON.parse(data);
          parsed.role = r.name;
          sessionStorage.setItem("user", JSON.stringify(parsed));
        } catch (e) { /* ignore */ }
      }
      this.showDropdown = false;
      window.location.reload();
    },
    async openPasswordModal() {
      this.showDropdown = false;
      this.pwdError = "";
      this.pwdSuccess = "";
      this.changeTarget = "self";
      this.pwdForm = {
        newPhone: this.user.phone || "",
        newName: this.user.fullName || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.showPasswordModal = true;

      // Agar super admin bo'lsa, boshqa adminlar ro'yxatini yuklash
      if (this.isSuperAdmin) {
        try {
          const res = await axios.get(`${process.env.VUE_APP_API_URL}/admin/all`);
          if (res.data && res.data.result) {
            this.allAdmins = res.data.result;
          }
        } catch (e) {
          console.warn("Could not fetch admins list", e);
        }
      }
    },
    onTargetChange() {
      this.pwdError = "";
      this.pwdSuccess = "";
      if (this.changeTarget === "self") {
        this.pwdForm.newPhone = this.user.phone || "";
        this.pwdForm.newName = this.user.fullName || "";
      } else {
        const found = this.allAdmins.find((a) => a._id === this.changeTarget);
        if (found) {
          this.pwdForm.newPhone = found.phone || "";
          this.pwdForm.newName = found.name || "";
        }
      }
      this.pwdForm.currentPassword = "";
      this.pwdForm.newPassword = "";
      this.pwdForm.confirmPassword = "";
    },
    closePasswordModal() {
      this.showPasswordModal = false;
      this.pwdError = "";
      this.pwdSuccess = "";
    },
    async handlePasswordChange() {
      this.pwdError = "";
      this.pwdSuccess = "";

      if (this.pwdForm.newPassword !== this.pwdForm.confirmPassword) {
        this.pwdError = "Yangi parol va tasdiqlash paroli bir-biriga mos kelmadi!";
        return;
      }

      if (this.pwdForm.newPassword.length < 4) {
        this.pwdError = "Parol uzunligi kamida 4 ta belgidan iborat bo'lishi kerak!";
        return;
      }

      this.pwdLoading = true;
      try {
        const payload = {
          currentPhone: this.user.phone,
          currentPassword: this.pwdForm.currentPassword,
          newPhone: this.pwdForm.newPhone,
          newName: this.pwdForm.newName,
          newPassword: this.pwdForm.newPassword,
          targetAdminId: this.changeTarget !== "self" ? this.changeTarget : null,
        };

        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/admin/change-credentials`,
          payload
        );

        if (res.data.success) {
          this.pwdSuccess = res.data.message || "Login va parol muvaffaqiyatli yangilandi!";

          // Agar o'zining parolini o'zgartirgan bo'lsa, lokal ma'lumotlarni yangilash
          if (this.changeTarget === "self") {
            if (res.data.token) {
              sessionStorage.setItem("token", res.data.token);
            }
            if (res.data.data) {
              this.user.phone = res.data.data.phone;
              this.user.fullName = res.data.data.name;
              sessionStorage.setItem("user", JSON.stringify(res.data.data));
            }
          }

          setTimeout(() => {
            this.closePasswordModal();
          }, 1800);
        } else {
          this.pwdError = res.data.message || "Xatolik yuz berdi";
        }
      } catch (err) {
        this.pwdError = err.response?.data?.message || err.message || "Parolni o'zgartirishda xatolik yuz berdi";
      } finally {
        this.pwdLoading = false;
      }
    },
    logout() {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      this.$router.push("/login");
    },
  },
  computed: {
    isOquvSuperAdmin() {
      const phone = (this.user?.phone || "").trim().toLowerCase();
      const name = (this.user?.fullName || "").trim().toLowerCase();
      return phone === "oquvsuperadmin" || name === "oquvsuper admin";
    },
    isSuperAdmin() {
      const roleLower = (this.user?.role || "").trim().toLowerCase();
      return ["super admin", "superadmin", "admin", "administrator"].includes(roleLower);
    },
    otherAdmins() {
      return this.allAdmins.filter(
        (a) => a.phone !== this.user.phone && a._id !== this.user.id
      );
    },
    homeRoute() {
      const roleLower = (this.user?.role || '').trim().toLowerCase();
      if (['super admin', 'superadmin', 'admin', 'administrator'].includes(roleLower)) {
        return '/';
      }
      if (['kafedra mudiri', 'kafedra_mudiri', 'mudir'].some(r => roleLower.includes(r))) {
        return { name: 'kafedra-taqsimoti' };
      }
      if (['dekan', 'dean', 'fakultet dekani', 'teacher', "o'qituvchi", 'assistent', 'assistant'].some(r => roleLower.includes(r)) || roleLower.includes('prorektor')) {
        return { name: 'shaxsiy-ish-reja' };
      }
      return '/';
    },
    breadcrumb() {
      return this.$route.meta?.breadcrumb || "";
    },
    userInitials() {
      return (this.user.fullName || "Admin")
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2);
    },
  },
};
</script>
